// FIXME: remove eventually

import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import equal from 'fast-deep-equal';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { calizaApi, useGetCustomerQuery } from '@/api/v1';
import { DynamicForm } from '@/components/organisms/DynamicForm';
import { StepperStep } from '@/components/organisms/StepperStep';
import { configuration } from '@/config';
import { IS_PRODUCTION_ENVIRONMENT } from '@/constants';
import { VerifyBusinessContext } from '@/context/verifyBusiness';
import { useStepperFormName } from '@/hooks/useStepperFormName';
import { useStepperStep } from '@/hooks/useStepperStep';
import { useVerifyBusinessSteps } from '@/hooks/useVerifyBusinessSteps';
import { computeStepsStatuses } from '@/Screens/VerifyBusiness/helpers';
import { statusesChanged } from '@/Screens/VerifyBusiness/reducer';
import { authSelector } from '@/StateManagement/AuthReducer';
import {
  Customer,
  CustomerPatchRequest,
  getCustomerSlice,
  isCustomerSliceType,
} from '@/types/customer';
import { createValidationSchema, Field, hiddenFieldsFilter } from '@/types/fields';
import { changeObserver } from '@/utility/changeObserver';
import { formLikeElementToObject } from '@/utility/formLikeElementToObject';
import { getStepSkipMessage } from '@/utility/getStepSkipMessage';
import { runWhenReady } from '@/utility/runWhenReady';
import { previewPatch, toPatchRequest } from '@/utility/stepPatcherHelpers';

export const VerifyBusinessFormContainer: React.FC = (): React.ReactElement => {
  const { user } = useSelector(authSelector);
  const { data: customer } = useGetCustomerQuery();

  const [, dispatch] = React.useContext(VerifyBusinessContext);

  const globalDispatch = useDispatch<any>();
  const steps = useVerifyBusinessSteps();
  const navigate = useNavigate();
  const formName = useStepperFormName();
  const step = useStepperStep(steps);
  if (!step) {
    // By going back, we trigger the correct catchall route
    navigate('.');
  }

  const value = React.useMemo<any>(
    (): any => getCustomerSlice(customer, step.type),
    [customer, step.type],
  );

  const skipMessage = React.useMemo(
    (): string | undefined => getStepSkipMessage(step, customer),
    [step, customer],
  );

  const updateStatuses = React.useCallback(
    (customer: Customer | undefined): void => {
      if (!customer) {
        return;
      }

      dispatch(statusesChanged(computeStepsStatuses(steps, customer)));
    },
    [dispatch, steps],
  );

  const executePatch = React.useCallback(
    (form: HTMLFormElement): void => {
      const sliceKey = step.type;

      if (isCustomerSliceType(sliceKey)) {
        // FIXME: need to fetch the customer because it might have changed
        //        locally.
        const patchRequest = toPatchRequest(sliceKey, formLikeElementToObject(form, step.fields));
        // Pre-patch to make sure we don't unnecessarily try to patch
        // in the backend
        const patchedCustomer = previewPatch(customer, patchRequest);
        if (!equal(patchedCustomer, customer)) {
          const applyPatch = async (patchRequest: CustomerPatchRequest): Promise<void> => {
            const response = await fetch(`${configuration.calizaAPIBaseURL}/v1/customer-signup`, {
              method: 'PATCH',
              body: JSON.stringify(patchRequest),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user?.accessToken}`,
              },
            });

            if (response.status !== 200) {
              console.debug('error patching customer');
              console.debug(await response.json());
            } else {
              // Do update the statuses and or any other side effects
              updateStatuses(patchedCustomer);
            }
          };

          runWhenReady((): void => {
            // Try to save the slice that has changed
            void applyPatch(patchRequest);
          });

          const { util } = calizaApi;
          const action = util.updateQueryData(
            'getCustomer',
            undefined,
            // React toolkit is a stupid toolkit that thinks
            // it knows better. And that type inference is better
            // than explicitly stating what thing is what type.
            //
            // IMHO this makes having types completely pointless.
            //
            // eslint-disable-next-line @typescript-eslint/typedef
            (draft): void => {
              Object.assign(draft, patchedCustomer);
            },
          );

          globalDispatch(action);
        } else if (!IS_PRODUCTION_ENVIRONMENT) {
          console.info('skipping patch: no change detected');
        }
      }
    },
    [customer, globalDispatch, step.fields, step.type, updateStatuses, user?.accessToken],
  );

  React.useEffect((): void => {
    updateStatuses(customer);
  }, [customer, updateStatuses]);

  React.useEffect((): VoidFunction | void => {
    const { forms } = document;

    if (!formName) {
      return;
    }

    const form = forms.namedItem(formName);
    if (!form) {
      return;
    }

    return changeObserver(form, step.fields, executePatch);
  }, [executePatch, formName, step.fields]);

  const filteredFields = React.useMemo((): readonly Field<unknown>[] => {
    const { fields } = step;

    return fields.filter(hiddenFieldsFilter(customer));
  }, [step, customer]);

  const validationSchema = React.useMemo(
    (): yup.ObjectSchema<yup.AnyObject, any> => createValidationSchema(filteredFields),
    [filteredFields],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      const { util } = calizaApi;
      globalDispatch(util.invalidateTags(['UserProfile']));
    };
  }, [globalDispatch]);

  if (skipMessage) {
    return (
      <StepperStep title={step.title} description={step.description}>
        <Box role="note" aria-label="skip note" sx={styles.skipMessageContainer}>
          <Typography fontSize={16} lineHeight="24px" fontWeight={500} color="custom.Inactive">
            {skipMessage}
          </Typography>
        </Box>
      </StepperStep>
    );
  }

  return (
    <StepperStep title={step.title} description={step.description}>
      <DynamicForm
        name={formName}
        value={value}
        validationSchema={validationSchema}
        fields={filteredFields}
      />
    </StepperStep>
  );
};

const styles: Record<string, SystemStyleObject<Theme>> = {
  skipMessageContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'custom.InputsBackground',
    px: 4,
    py: 3,
    borderRadius: '8px',
    height: 88,
  },
};
